import { pinnedContracts, networkBuild } from '@hoodmorning/config';
import { bytes32Schema, nonzeroAddressSchema, uint256StringSchema, type Address, type Hex } from '@hoodmorning/contracts';
import { decodeEventLog, decodeFunctionData, getAddress, isAddressEqual, parseAbi, toEventSelector, zeroAddress, type TransactionReceipt } from 'viem';
import { entryPoint07Abi, getUserOperationHash, toUserOperation } from 'viem/account-abstraction';
import { ownerUserOperationEventAbi } from './userOperationReceipt.js';
import { z } from 'zod';

// Installed ox.from unpacks correctly at runtime but its generic return type
// preserves the packed input shape. Validate its result instead of asserting it.
const quantity = z.bigint().min(0n).max((1n << 256n) - 1n);
const hexBytes = z.templateLiteral(['0x', z.string()]).refine(value => /^0x(?:[0-9a-fA-F]{2})*$/.test(value));
const address = nonzeroAddressSchema.transform(value => getAddress(value));
const unpackedSchema = z.object({ sender: address, nonce: quantity, callData: hexBytes,
  callGasLimit: quantity, verificationGasLimit: quantity, preVerificationGas: quantity,
  maxFeePerGas: quantity, maxPriorityFeePerGas: quantity, signature: hexBytes,
  factory: address.optional(), factoryData: hexBytes.optional(),
  paymaster: address.optional(), paymasterData: hexBytes.optional(),
  paymasterVerificationGasLimit: quantity.optional(), paymasterPostOpGasLimit: quantity.optional() });

const markerAbi = parseAbi(['event BeforeExecution()', 'event SignatureAggregatorChanged(address indexed aggregator)']);
const beforeTopic = toEventSelector(markerAbi[0]);
const aggregatorTopic = toEventSelector(markerAbi[1]);
const operationTopic = toEventSelector(ownerUserOperationEventAbi[0]);
const failureAbi = parseAbi([
  'event UserOperationRevertReason(bytes32 indexed userOpHash,address indexed sender,uint256 nonce,bytes revertReason)',
  'event PostOpRevertReason(bytes32 indexed userOpHash,address indexed sender,uint256 nonce,bytes revertReason)',
  'event UserOperationPrefundTooLow(bytes32 indexed userOpHash,address indexed sender,uint256 nonce)',
]);
const failureTopics = new Set(failureAbi.map(event => toEventSelector(event)));

/** Structural extraction for the pinned EntryPoint 0.7 direct handleOps profile.
 * Caller MUST independently verify canonical receipt completeness, transaction
 * inclusion and historical EntryPoint/account code. Logs are candidates, not
 * authenticated token movements, accepted budgets, trade attribution or P&L.
 * Validation logs precede BeforeExecution; beneficiary callbacks follow the final
 * UserOperationEvent. Neither belongs to an operation's execution segment.
 */
type ExecutionLogsInput = {
  receipt: TransactionReceipt;
  transaction: { hash: Hex; to: Address | null; input: Hex; blockHash: Hex | null; blockNumber: bigint | null; transactionIndex: number | null };
  expectedHash: Hex; sender: Address; nonce: bigint;
};

export function extractOwnerOperationExecutionLogs(input: ExecutionLogsInput) {
  return extractForChain(input, 4663);
}

/** Only the immutable build selects this structural operation-hash domain.
 * Canonical/provider/code verification remains the caller's responsibility. */
export function extractSelectedOwnerOperationExecutionLogs(input: ExecutionLogsInput) {
  return extractForChain(input, networkBuild.chainId);
}

function extractForChain(input: ExecutionLogsInput, chainId: 4663 | 46630) {
  bytes32Schema.parse(input.expectedHash); nonzeroAddressSchema.parse(input.sender);
  uint256StringSchema.parse(input.nonce.toString());
  const { receipt, transaction } = input;
  const ep = pinnedContracts.entryPointV07.address;
  if (!transaction.to || !isAddressEqual(transaction.to, ep) || !receipt.to || !isAddressEqual(receipt.to, ep)
    || receipt.status !== 'success' || transaction.hash !== receipt.transactionHash
    || transaction.blockHash !== receipt.blockHash || transaction.blockNumber !== receipt.blockNumber
    || transaction.transactionIndex !== receipt.transactionIndex) throw new Error('Direct canonical EntryPoint transaction required');
  const decoded = decodeFunctionData({ abi: entryPoint07Abi, data: transaction.input });
  if (decoded.functionName !== 'handleOps') throw new Error('Only direct handleOps execution is supported');
  const operations = decoded.args[0];
  if (operations.length === 0) throw new Error('Empty operation bundle');
  const unpacked = operations.map(operation => unpackedSchema.parse(toUserOperation(operation)));
  const hashes = unpacked.map(operation => getUserOperationHash({ userOperation: operation,
    chainId, entryPointAddress: ep, entryPointVersion: '0.7' }));
  if (new Set(hashes.map(hash => hash.toLowerCase())).size !== hashes.length) throw new Error('Duplicate bundle operation hash');
  const selected = hashes.findIndex(hash => hash.toLowerCase() === input.expectedHash.toLowerCase());
  if (selected === -1 || !isAddressEqual(operations[selected]!.sender, input.sender) || operations[selected]!.nonce !== input.nonce
    || operations[selected]!.paymasterAndData !== '0x') throw new Error('Target operation identity or paymaster mismatch');
  let lastIndex = -1;
  for (const log of receipt.logs) {
    if (log.removed || log.blockHash !== receipt.blockHash || log.blockNumber !== receipt.blockNumber
      || log.transactionHash !== receipt.transactionHash || log.transactionIndex !== receipt.transactionIndex
      || log.logIndex === null || !Number.isSafeInteger(log.logIndex) || log.logIndex <= lastIndex) throw new Error('Incomplete or unordered receipt log identity');
    // A complete receipt has consecutive global log indices, even when its first index is nonzero.
    if (lastIndex !== -1 && log.logIndex !== lastIndex + 1) throw new Error('Receipt log gap');
    lastIndex = log.logIndex;
  }
  const emittedByEp = receipt.logs.filter(log => isAddressEqual(log.address, ep));
  const markers = emittedByEp.filter(log => log.topics[0] === beforeTopic);
  if (markers.length !== 1 || markers[0]!.topics.length !== 1 || markers[0]!.data !== '0x') throw new Error('Exactly one canonical BeforeExecution marker required');
  if (emittedByEp.some(log => log.topics[0] === aggregatorTopic)) throw new Error('Aggregated execution is unsupported');
  const terminalLogs = emittedByEp.filter(log => log.topics[0] === operationTopic);
  if (terminalLogs.length !== operations.length) throw new Error('Bundle terminal event count mismatch');
  const events = terminalLogs.map((log, i) => {
    if (log.topics.length !== 4 || log.data.length !== 2 + 64 * 4) throw new Error('Malformed operation terminal event');
    const event = decodeEventLog({ abi: ownerUserOperationEventAbi, topics: log.topics, data: log.data, strict: true }).args;
    const operation = unpacked[i]!;
    if (log.logIndex! <= markers[0]!.logIndex! || event.userOpHash.toLowerCase() !== hashes[i]!.toLowerCase()
      || !isAddressEqual(event.sender, operation.sender) || event.nonce !== operation.nonce
      || !isAddressEqual(event.paymaster, operation.paymaster ?? zeroAddress)) throw new Error('Bundle event ordering or identity mismatch');
    return event;
  });
  const start = selected === 0 ? markers[0]!.logIndex! : terminalLogs[selected - 1]!.logIndex!;
  const end = terminalLogs[selected]!.logIndex!;
  const segment = receipt.logs.filter(log => log.logIndex! > start && log.logIndex! < end);
  const event = events[selected]!;
  for (const log of segment) {
    if (!isAddressEqual(log.address, ep) || !log.topics[0] || !failureTopics.has(log.topics[0])) continue;
    const reason = decodeEventLog({ abi: failureAbi, topics: log.topics, data: log.data, strict: true }).args;
    if (event.success || reason.userOpHash.toLowerCase() !== input.expectedHash.toLowerCase()
      || !isAddressEqual(reason.sender, input.sender) || reason.nonce !== input.nonce) throw new Error('Inconsistent operation failure evidence');
  }
  return { userOperationHash: hashes[selected]!, sender: event.sender, nonce: event.nonce, success: event.success,
    actualGasCost: event.actualGasCost, actualGasUsed: event.actualGasUsed,
    validationEndLogIndex: markers[0]!.logIndex!, executionStartLogIndex: start, userOperationLogIndex: end,
    candidateLogs: event.success ? structuredClone(segment.filter(log => !isAddressEqual(log.address, ep))) : [],
    selectedCallData: unpacked[selected]!.callData,
    // Parsed from the selected actual transaction operation, not a caller's
    // preparation or another operation in this bundle. Never expose signatures.
    ...(unpacked[selected]!.factory === undefined ? {} : { selectedFactory: unpacked[selected]!.factory }),
    ...(unpacked[selected]!.factoryData === undefined ? {} : { selectedFactoryData: unpacked[selected]!.factoryData }),
    source: 'direct-entrypoint-0.7-handleOps-structural-segment' as const,
    verification: 'canonicality-code-token-semantics-and-accounting-required' as const };
}
